import { alertAuthExpired, getFfdUrl } from "shared/api/ffdClient";

const extractFileName = (contentDisposition: string | null, fallbackName: string): string => {
    let fileName = `${fallbackName}.csv`;

    if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
        const bareMatch = contentDisposition.match(/filename=([^;]+)/i);
        const rawName = (utf8Match?.[1] ?? quotedMatch?.[1] ?? bareMatch?.[1])?.trim();
        if (rawName) {
            fileName = decodeURIComponent(rawName.replace(/^"|"$/g, ""));
        }
    }

    if (!fileName.toLowerCase().endsWith(".csv")) {
        fileName = `${fileName}.csv`;
    }

    return fileName;
};

export const fetchStudyCsvData = async (
    authToken: string,
    name: string
): Promise<boolean> => {
    try {
        const response = await fetch(
            getFfdUrl(`/studies/download?study_name=${encodeURIComponent(name)}`),
            {
                headers: {
                    Accept: "text/csv",
                    Authorization: authToken,
                },
            }
        );
        if (response.ok) {
            const csvBody = await response.text();
            if (!csvBody) {
                alert("CSV data is empty.");
                return false;
            }

            const fileName = extractFileName(
                response.headers.get("Content-Disposition"),
                name
            );
            const blob = new Blob([csvBody], { type: "text/csv;charset=utf-8" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            return true;
        }

        if (response.status === 401 || response.status === 403) {
            alertAuthExpired();
            return false;
        }

        const contentType = response.headers.get("Content-Type") ?? "";
        if (contentType.includes("application/json")) {
            const data = await response.json().catch(() => null);
            const message =
                (data?.text as string | undefined) ??
                (data?.message as string | undefined);
            if (message) {
                alert(message);
                return false;
            }
        } else {
            const text = await response.text().catch(() => "");
            if (text) {
                alert(text);
                return false;
            }
        }

        alert(`Failed to download CSV. (status: ${response.status})`);
        return false;
    } catch (error) {
        alertAuthExpired();
        return false;
    }
};
